import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { STAFF } from "@/constants/testIds";
import { Briefcase, MapPin, Clock, Euro } from "lucide-react";

function ApplyDialog({ job, onDone }) {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState({ a: false, l: false, s: false, t: false });
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!c.a || !c.l || !c.s || !c.t) return toast.error("Please confirm all four checkboxes");
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/apply`, {
        confirm_available: c.a, confirm_licence: c.l, confirm_sops: c.s, accept_terms: c.t,
      });
      toast.success("Application submitted");
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-white" data-testid={`apply-btn-${job.id}`}>Apply</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirm & Apply — {job.title}</DialogTitle></DialogHeader>
        <div className="text-sm text-white/70 space-y-3 font-manrope">
          {[
            ["a", "I confirm I am available for the date and times listed."],
            ["l", "I confirm I hold the required licence/qualification."],
            ["s", "I confirm I have read and understood the SOPs and site instructions."],
            ["t", "I agree to the job terms and payment conditions."],
          ].map(([k, label]) => (
            <label key={k} className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={c[k]} onCheckedChange={(v) => setC({ ...c, [k]: !!v })} data-testid={`apply-check-${k}`} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="border-t pt-3 text-xs text-white/60 font-manrope">
          <div><strong>SOPs:</strong> {job.sops || "—"}</div>
          <div><strong>Dress code:</strong> {job.dress_code || "—"}</div>
        </div>
        <DialogFooter>
          <Button onClick={apply} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="apply-confirm-btn">Submit Application</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StaffJobs() {
  const [jobs, setJobs] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid={STAFF.jobsList}>
      <h1 className="text-3xl font-outfit font-bold text-white">Jobs Board</h1>
      {jobs.length === 0 && (
        <div className="card-flat p-8 text-center text-white/60 font-manrope">No open jobs at the moment.</div>
      )}
      <div className="grid gap-4">
        {jobs.map((j) => (
          <div key={j.id} className="card-flat p-5 border-l-4 border-l-brand" data-testid={`job-card-${j.id}`}>
            <div className="flex flex-wrap justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  {j.job_number && (
                    <span className="text-[10px] uppercase tracking-widest font-outfit font-bold text-brand bg-brand/10 border border-brand/40 rounded px-1.5 py-0.5" data-testid={`job-number-${j.id}`}>
                      {j.job_number}
                    </span>
                  )}
                  <div className="font-outfit font-semibold text-white text-lg">{j.title}</div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-white/60 font-manrope mt-1">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {j.date} · {j.start_time}–{j.end_time}</span>
                  <span className="flex items-center gap-1"><Euro className="w-3.5 h-3.5" /> {j.rate_of_pay}/hr</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.required_role}</span>
                </div>
              </div>
              <ApplyDialog job={j} onDone={load} />
            </div>
            {j.site_instructions && <div className="text-sm text-white/70 font-manrope"><strong>Site:</strong> {j.site_instructions}</div>}
            {j.dress_code && <div className="text-sm text-white/70 font-manrope"><strong>Dress:</strong> {j.dress_code}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
